import {
  AddButtonWrapper,
  ForenameColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  OptionalIndicator,
  RemoveGuestButtonWrapper,
  SurnameColumn,
  TitleColumn,
} from '@common/components/BookingForms/GuestField/GuestField.styles';
import RemoveGuestButton from '@common/components/BookingForms/GuestField/RemoveGuestButton';
import { Button } from '@common/components/Button';
import { BlockInputField } from '@common/components/FormElements';
import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { useTranslations } from '@lang';
import { AddIcon } from '@root/styles/icons';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { useEffect } from 'react';
import { Controller, UseFieldArrayReturn, useFormContext, useWatch } from 'react-hook-form';
import { RoomRateFormValues } from '../RoomRateForm.schema';

type MultiRoomGuestFieldProps = {
  fieldArray: UseFieldArrayReturn<RoomRateFormValues, 'guests', 'id'>;
  roomNumber: number;
  roomAdults: number;
  minimumGuests: number;
  maximumGuests?: number;
  withLeadGuest?: boolean;
};

export const MultiRoomGuestField = ({
  fieldArray,
  roomNumber,
  roomAdults,
  minimumGuests,
  maximumGuests,
  withLeadGuest = false,
}: MultiRoomGuestFieldProps) => {
  const { t } = useTranslations();
  const { control, setValue } = useFormContext<RoomRateFormValues>();
  const { fields, append, remove } = fieldArray;
  const guestValues = useWatch({ control, name: 'guests' }) ?? [];

  const titlesOptions = [...maleTitlesOptions, ...femaleTitlesOptions].map((option) => ({
    ...option,
    label: t(option.label),
  }));

  useEffect(() => {
    const roomSlots = guestValues.filter((g) => g.room === roomNumber).length;
    const slotsNeeded = Math.max(0, roomAdults - roomSlots);
    if (slotsNeeded > 0) {
      for (let i = 0; i < slotsNeeded; i++) {
        append({ room: roomNumber, title: '', firstName: '', lastName: '', gender: '' });
      }
    }
  }, [roomNumber, roomAdults]);

  const roomGuestIndices = fields
    .map((f, i) => ({ index: i, data: guestValues[i] }))
    .filter(({ data }) => data?.room === roomNumber);

  const handleRemove = (absoluteIndex: number) => {
    remove(absoluteIndex);
    const remainingRoomSlots = guestValues.filter(
      (g, i) => g.room === roomNumber && i !== absoluteIndex
    ).length;
    if (remainingRoomSlots < minimumGuests) {
      append({ room: roomNumber, title: '', firstName: '', lastName: '', gender: '' });
    }
  };

  const handleAdd = () => {
    const currentRoomCount = roomGuestIndices.length;
    if (!maximumGuests || currentRoomCount < maximumGuests) {
      append({ room: roomNumber, title: '', firstName: '', lastName: '', gender: '' });
    }
  };

  const canAddNextGuest = !maximumGuests || roomGuestIndices.length < maximumGuests;

  return (
    <>
      {roomGuestIndices.map(({ index: absoluteIndex }, roomIndex) => {
        const guestData = guestValues[absoluteIndex] ?? {};
        const isExistingBasketGuest = !!(guestData.externalSpaId || guestData.id);
        const isGenderDataMissing = !(guestData.title || guestData.gender);
        const isOptionalIndicatorVisible = roomIndex > 0 && withLeadGuest;
        const isFieldRequired = roomIndex === 0;

        return (
          <GuestRow key={fields[absoluteIndex]?.id ?? absoluteIndex}>
            <GuestCounter>
              {withLeadGuest && roomIndex === 0
                ? t('common.leadGuest')
                : t('spa.guestCounter', { counter: roomIndex + 1 })}
              {isOptionalIndicatorVisible && (
                <OptionalIndicator> {t('common.optional')}</OptionalIndicator>
              )}
            </GuestCounter>
            <InputsRow>
              <TitleColumn>
                <Controller
                  control={control}
                  name={`guests.${absoluteIndex}.title`}
                  render={({ field: { onChange, value } }) => (
                    <BlockSelectField
                      useNativeDropdownOnMobile={false}
                      labelWidth="50px"
                      options={titlesOptions}
                      value={titlesOptions.find((opt) => opt.value === value) ?? null}
                      label={t('common.title')}
                      isRequired={isFieldRequired}
                      isDisabled={isExistingBasketGuest && !isGenderDataMissing}
                      isSearchable={false}
                      id={`${absoluteIndex}-title`}
                      onChange={(selected: any) => {
                        const val = selected?.value ?? '';
                        onChange(val);
                        setValue(
                          `guests.${absoluteIndex}.gender`,
                          val === 'Mr' ? 'male' : 'female'
                        );
                      }}
                    />
                  )}
                />
              </TitleColumn>
              <ForenameColumn>
                <Controller
                  control={control}
                  name={`guests.${absoluteIndex}.firstName`}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField
                      isRequired={isFieldRequired}
                      label={t('common.firstName')}
                      placeholder={t('common.firstName')}
                      disabled={isExistingBasketGuest}
                      id={`${absoluteIndex}-fname`}
                      value={value ?? ''}
                      onChange={onChange}
                    />
                  )}
                />
              </ForenameColumn>
              <SurnameColumn>
                <Controller
                  control={control}
                  name={`guests.${absoluteIndex}.lastName`}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField
                      isRequired={isFieldRequired}
                      label={t('common.lastName')}
                      placeholder={t('common.lastName')}
                      disabled={isExistingBasketGuest}
                      id={`${absoluteIndex}-lname`}
                      value={value ?? ''}
                      onChange={onChange}
                    />
                  )}
                />
              </SurnameColumn>
              <RemoveGuestButtonWrapper>
                <RemoveGuestButton
                  isVisible={isExistingBasketGuest || roomIndex >= minimumGuests}
                  onClick={() => handleRemove(absoluteIndex)}
                />
              </RemoveGuestButtonWrapper>
            </InputsRow>
          </GuestRow>
        );
      })}
      <AddButtonWrapper>
        {canAddNextGuest && (
          <Button
            type="button"
            isSmall
            onClick={handleAdd}
            icon={<AddIcon width="16" height="16" />}
          >
            {t('common.addMoreGuests')}
          </Button>
        )}
      </AddButtonWrapper>
    </>
  );
};
